import React from "react";
import type { ColorToken } from "../../theme/styled";
import { keyframes, styled } from "../../theme/styled";

type Sizes = "small" | "medium" | "large";

interface Props {
  size?: Sizes;
  ariaLabel?: string;
  color?: ColorToken | "currentColor";
  className?: string;
}

interface SVGProps {
  thickness: number;
  loaderSize: number;
}

/**
 * Based on @material-ui/core/CircularProgress with some tweaks.
 */
export const Loader = ({
  size = "large",
  ariaLabel,
  color = "$border-distinct",
  className,
}: Props): React.JSX.Element => {
  const getLoaderStyles = (size: Sizes): SVGProps => {
    const styles: Record<Sizes, SVGProps> = {
      small: {
        loaderSize: 16,
        thickness: 2,
      },
      medium: {
        loaderSize: 24,
        thickness: 2,
      },
      large: {
        loaderSize: 40,
        thickness: 3,
      },
    };

    return styles[size];
  };

  const { loaderSize, thickness } = getLoaderStyles(size);

  const radius = (44 - thickness) / 2;
  const centerCoordinates = 44;

  return (
    <Wrapper
      size={loaderSize}
      className={className}
      role="progressbar"
      aria-label={ariaLabel}
      data-component-type="Loader"
    >
      <svg viewBox="22 22 44 44">
        <Circle
          color={color}
          thickness={thickness}
          fill="none"
          cx={centerCoordinates}
          cy={centerCoordinates}
          r={radius}
          strokeLinecap="round"
        />
      </svg>
    </Wrapper>
  );
};

const rotator = keyframes({
  "100%": {
    transform: "rotate(360deg)",
  },
});

const Wrapper = styled.div<{ size: SVGProps["loaderSize"] }>((p) => ({
  animation: `${rotator} 2s linear infinite`,
  width: p.size,
  height: p.size,
}));

const Circle = styled.circle<{
  color: Props["color"];
  thickness: SVGProps["thickness"];
}>((p) => {
  const spin = keyframes({
    "0%": {
      strokeDasharray: "1, 200",
      strokeDashoffset: "0",
    },
    "50%": {
      strokeDasharray: "100, 200",
      strokeDashoffset: "-15",
    },
    "100%": {
      strokeDasharray: "100, 200",
      strokeDashoffset: "-125",
    },
  });

  return {
    strokeWidth: p.thickness,
    strokeDasharray: "80, 200",
    strokeDashoffset: "0",
    stroke: p.color,
    animation: `${spin} 1.5s ease-in-out infinite`,
  };
});
